<script setup lang="ts">
import { computed } from 'vue';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IWelcomePageRedirectsForm } from '@modules/welcome-page-redirects/types';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { TRANSLATION_GROUP_WELCOME_PAGE_REDIRECTS, WELCOME_PAGE_REDIRECTS_PERMISSIONS } from '@modules/welcome-page-redirects/constants';
import { useSaveWelcomePageRedirectsMutation } from '@modules/welcome-page-redirects/mutations';
import { useWelcomePageRedirectsQuery } from '@modules/welcome-page-redirects/queries';

const { getTranslation } = useI18n();

const { data, isPending } = useWelcomePageRedirectsQuery();

const { mutate, isLoading, errors, clearError } = useSaveWelcomePageRedirectsMutation();

const formSchema = computed<FormSchema<IWelcomePageRedirectsForm>>(() => ({
  enable: {
    type: EFormInputTypes.SWITCH,
    label: getTranslation(TRANSLATION_GROUP_WELCOME_PAGE_REDIRECTS, 'WELCOME_PAGE_REDIRECTS_ENABLE_REDIRECT').value,
    value: data.value?.enable,
  },
  url: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_WELCOME_PAGE_REDIRECTS, 'WELCOME_PAGE_REDIRECTS_URI').value,
    value: data.value?.url,
  },
}));

function onSubmit(values: IWelcomePageRedirectsForm): void {
  mutate({ welcome_page_redirects: values });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'WelcomePageRedirectsPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_WELCOME_PAGE_REDIRECTS, 'WELCOME_PAGE_REDIRECTS_HEADER')"
  >
    <VLoader
      :loading="isPending"
      min-height="190px"
    >
      <VForm
        :schema="formSchema"
        :is-loading="isLoading"
        :custom-errors="errors"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_WELCOME_PAGE_REDIRECTS, 'WELCOME_PAGE_REDIRECTS_SAVE_BUTTON')"
        :readonly="!$hasPermission(WELCOME_PAGE_REDIRECTS_PERMISSIONS.EDIT)"
        @submit="onSubmit"
        @interacted="clearError"
      />
    </VLoader>
  </VCard>
</template>
